/** Phase 14 Wave 3 — Module J — Announcements admin page. */
import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Loader2, Plus, AlertTriangle, Trash2 } from "lucide-react";
import { clearFeatureFlagCache } from "@/hooks/useFeatureFlag";

const LEVELS = ["info", "warning", "critical"];

export default function AdminAnnouncements() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState({ message: "", level: "info", starts_at: "", ends_at: "", dismissible: true });
  const [saving, setSaving] = useState(false);
  const [maintFlag, setMaintFlag] = useState(null);
  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [a, f] = await Promise.all([
        api.get("/admin/announcements"),
        api.get("/admin/feature-flags"),
      ]);
      setItems(a.data.items || []);
      const ff = (f.data.items || []).find((x) => x.key === "maintenance_mode_enabled");
      setMaintFlag(ff || null);
    } finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);
  const create = async () => {
    if (!form.message.trim()) return toast.error("Message required");
    setSaving(true);
    try {
      await api.post("/admin/announcements", {
        ...form,
        starts_at: form.starts_at || null,
        ends_at: form.ends_at || null,
      });
      toast.success("Announcement created");
      setForm({ message: "", level: "info", starts_at: "", ends_at: "", dismissible: true });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Create failed"); }
    finally { setSaving(false); }
  };
  const del = async (id) => {
    try {
      await api.delete(`/admin/announcements/${id}`);
      toast.success("Deleted");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };
  const toggleMaint = async (next) => {
    try {
      await api.patch("/admin/feature-flags/maintenance_mode_enabled", { enabled: next });
      clearFeatureFlagCache();
      toast.success(next ? "🔴 Maintenance mode ENABLED" : "✅ Maintenance mode disabled");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Toggle failed"); }
  };

  return (
    <div className="space-y-6" data-testid="admin-announcements">
      <div>
        <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-indigo-600">Module J</p>
        <h1 className="mt-1 text-2xl font-bold tracking-tight text-slate-900">Announcements & Maintenance</h1>
        <p className="mt-1 text-sm text-slate-500">Site-wide banners and the kill-switch for maintenance mode.</p>
      </div>

      {/* Maintenance mode kill-switch */}
      <div className={`rounded-2xl border-2 p-5 shadow-sm ${maintFlag?.enabled ? "border-red-300 bg-red-50" : "border-slate-200 bg-white"}`}
           data-testid="maintenance-mode-card">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-start gap-3">
            <div className={`inline-flex h-10 w-10 items-center justify-center rounded-full ${maintFlag?.enabled ? "bg-red-600 text-white" : "bg-slate-100 text-slate-500"}`}>
              <AlertTriangle className="h-5 w-5" />
            </div>
            <div>
              <p className="text-base font-bold text-slate-900">Maintenance mode</p>
              <p className="text-xs text-slate-500">
                When enabled, every /api/* route returns 503 except /api/auth/* and /api/admin/*.
                Admins can still log in and toggle it off.
              </p>
            </div>
          </div>
          <Switch
            checked={!!maintFlag?.enabled}
            onCheckedChange={toggleMaint}
            data-testid="maintenance-mode-toggle"
          />
        </div>
      </div>

      {/* Create form */}
      <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-sm">
        <h2 className="text-sm font-semibold text-slate-900">New announcement</h2>
        <div className="mt-3 grid gap-3 sm:grid-cols-2">
          <div className="sm:col-span-2">
            <Label className="text-xs">Message</Label>
            <Textarea data-testid="ann-message" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} rows={2} className="mt-1 text-sm" />
          </div>
          <div>
            <Label className="text-xs">Level</Label>
            <div className="mt-1 flex gap-1.5">
              {LEVELS.map((l) => (
                <button key={l} type="button" data-testid={`ann-level-${l}`}
                  onClick={() => setForm({ ...form, level: l })}
                  className={`rounded-md border px-3 py-1.5 text-xs font-semibold uppercase ${form.level === l ? "border-indigo-600 bg-indigo-50 text-indigo-700" : "border-slate-200 text-slate-600 hover:bg-slate-50"}`}>{l}</button>
              ))}
            </div>
          </div>
          <div>
            <Label className="text-xs">Starts (ISO)</Label>
            <Input type="datetime-local" value={form.starts_at} onChange={(e) => setForm({ ...form, starts_at: e.target.value ? `${e.target.value}:00.000Z` : "" })} className="h-9 mt-1" />
          </div>
          <div>
            <Label className="text-xs">Ends (ISO)</Label>
            <Input type="datetime-local" value={form.ends_at} onChange={(e) => setForm({ ...form, ends_at: e.target.value ? `${e.target.value}:00.000Z` : "" })} className="h-9 mt-1" />
          </div>
          <div className="flex items-center gap-2">
            <Switch checked={form.dismissible} onCheckedChange={(v) => setForm({ ...form, dismissible: v })} data-testid="ann-dismissible" />
            <Label className="text-xs">Dismissible</Label>
          </div>
        </div>
        <div className="mt-3 flex justify-end">
          <Button onClick={create} disabled={saving} data-testid="ann-create" className="bg-indigo-600 text-white hover:bg-indigo-700">
            {saving ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <Plus className="mr-1 h-3.5 w-3.5" />} Create
          </Button>
        </div>
      </div>

      {/* List */}
      <div className="space-y-2">
        <h2 className="text-xs font-semibold uppercase tracking-wider text-slate-500">All announcements</h2>
        <div className="overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm">
          {loading ? <div className="p-8 text-center text-slate-400"><Loader2 className="mx-auto h-5 w-5 animate-spin" /></div>
           : items.length === 0 ? <div className="p-8 text-center text-sm text-slate-400">No announcements yet.</div>
           : items.map((a) => (
            <div key={a.id} data-testid={`ann-row-${a.id}`} className="flex items-center justify-between gap-3 border-b border-slate-100 px-4 py-3 last:border-b-0">
              <div className="min-w-0">
                <p className="flex items-center gap-2 text-sm font-medium">
                  <span className={`rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase ${a.level === "critical" ? "bg-red-100 text-red-700" : a.level === "warning" ? "bg-amber-100 text-amber-700" : "bg-blue-100 text-blue-700"}`}>{a.level}</span>
                  {a.message}
                </p>
                <p className="mt-0.5 text-[10px] text-slate-400">
                  {(a.starts_at || "").slice(0, 16)} → {(a.ends_at || "no end").slice(0, 16)} · dismissible={String(a.dismissible)}
                </p>
              </div>
              <button onClick={() => del(a.id)} data-testid={`ann-delete-${a.id}`} className="rounded-md p-1.5 text-red-500 hover:bg-red-50">
                <Trash2 className="h-3.5 w-3.5" />
              </button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
